/**
 * SKOP Trading Module
 *
 * Unified trading interface for all supported platforms:
 * - pump.fun (bonding curve)
 * - LetsBONK (bonding curve)
 * - Raydium LaunchLab (bonding curve)
 * - Jupiter (graduated tokens / DEX aggregator)
 */

import {
  Connection,
  PublicKey,
  Transaction,
  VersionedTransaction,
  TransactionInstruction,
  SystemProgram,
  LAMPORTS_PER_SOL,
  ComputeBudgetProgram,
} from '@solana/web3.js';
import { getAssociatedTokenAddress } from '@solana/spl-token';

// Platform imports
import * as PumpFun from './platforms/pump-fun';
import * as PumpSwap from './platforms/pumpswap';
import * as LetsBonk from './platforms/letsbonk';
import * as Raydium from './platforms/raydium';
import * as Jupiter from './platforms/jupiter';

// Fee & analytics imports
import { TRADE_FEE_BPS, trackTrade } from './subscription';

// ============================================================================
// Configuration
// ============================================================================

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.skop.site';
const RPC_URL = `${API_BASE}/api/rpc`;
const SKOP_FEE_WALLET = new PublicKey(
  import.meta.env.VITE_SKOP_FEE_WALLET || '11111111111111111111111111111111'
);

// Priority fee levels in microlamports per compute unit
export const PRIORITY_FEES = {
  none: 0,
  low: 10000,      // ~0.0001 SOL for 200k CU
  medium: 50000,   // ~0.0005 SOL
  high: 100000,    // ~0.001 SOL
  turbo: 500000,   // ~0.005 SOL
} as const;

export type PriorityLevel = keyof typeof PRIORITY_FEES;
export type Platform = 'pump.fun' | 'pumpswap' | 'letsbonk' | 'raydium' | 'jupiter' | 'unknown';

// ============================================================================
// Types
// ============================================================================

export interface TradeParams {
  tokenAddress: string;
  action: 'buy' | 'sell';
  amount: number; // SOL for buy, tokens for sell
  userWallet: PublicKey;
  signTransaction: <T extends Transaction | VersionedTransaction>(tx: T) => Promise<T>;
  slippageBps: number;
  priorityLevel?: PriorityLevel;
  platform?: Platform; // Auto-detected if not provided
}

export interface TradeResult {
  signature: string;
  success: boolean;
  expectedOutput: string;
  minOutput: string;
  priceImpact: number;
  platform: Platform;
  explorerUrl: string;
}

export interface TradeQuote {
  expectedOutput: bigint;
  minOutput: bigint;
  priceImpact: number;
  isGraduated: boolean;
  platform: Platform;
  route?: string; // For Jupiter, shows route path
}

export interface PlatformInfo {
  platform: Platform;
  isGraduated: boolean;
  bondingCurveProgress?: number; // 0-100%
}

// ============================================================================
// Connection
// ============================================================================

const connection = new Connection(RPC_URL, 'confirmed');

export function getConnection(): Connection {
  return connection;
}

// ============================================================================
// Platform Detection
// ============================================================================

/**
 * Auto-detect which platform a token belongs to
 */
export async function detectPlatform(tokenAddress: string): Promise<PlatformInfo> {
  const mint = new PublicKey(tokenAddress);

  // Check pump.fun first (most common)
  try {
    const isPump = await PumpFun.isPumpFunToken(connection, mint);
    if (isPump) {
      const graduated = await PumpFun.isGraduated(connection, mint);
      if (graduated) {
        // Check if on PumpSwap (pump.fun's native AMM)
        const hasPumpSwap = await PumpSwap.hasPumpSwapPool(connection, mint);
        return {
          platform: hasPumpSwap ? 'pumpswap' : 'jupiter',
          isGraduated: true,
        };
      }
      return {
        platform: 'pump.fun',
        isGraduated: false,
      };
    }
  } catch (e) {
    console.debug('pump.fun check failed:', e);
  }

  // Check LetsBONK
  try {
    const isBonk = await LetsBonk.isLetsBonkToken(connection, mint);
    if (isBonk) {
      const graduated = await LetsBonk.isGraduated(connection, mint);
      return {
        platform: graduated ? 'jupiter' : 'letsbonk',
        isGraduated: graduated,
      };
    }
  } catch (e) {
    console.debug('LetsBONK check failed:', e);
  }

  // Check Raydium LaunchLab
  try {
    const isLaunchLab = await Raydium.isLaunchLabToken(connection, mint);
    if (isLaunchLab) {
      const graduated = await Raydium.isGraduated(connection, mint);
      return {
        platform: graduated ? 'jupiter' : 'raydium',
        isGraduated: graduated,
      };
    }
  } catch (e) {
    console.debug('Raydium LaunchLab check failed:', e);
  }

  // Check if tradeable on Jupiter (fallback for any DEX token)
  try {
    const hasLiquidity = await Jupiter.hasJupiterLiquidity(tokenAddress);
    if (hasLiquidity) {
      return {
        platform: 'jupiter',
        isGraduated: true,
      };
    }
  } catch (e) {
    console.debug('Jupiter check failed:', e);
  }

  return {
    platform: 'unknown',
    isGraduated: false,
  };
}

// ============================================================================
// Quote Functions
// ============================================================================

/**
 * Get a quote for a trade
 */
export async function getTradeQuote(params: {
  tokenAddress: string;
  action: 'buy' | 'sell';
  amount: number;
  slippageBps: number;
  platform?: Platform;
}): Promise<TradeQuote> {
  const { tokenAddress, action, amount, slippageBps } = params;
  let { platform } = params;
  const mint = new PublicKey(tokenAddress);

  // Auto-detect platform if not provided
  if (!platform || platform === 'unknown') {
    const detected = await detectPlatform(tokenAddress);
    platform = detected.platform;
  }

  // Convert amount to proper units
  const solLamports = BigInt(Math.floor(amount * LAMPORTS_PER_SOL));
  const tokenUnits = BigInt(Math.floor(amount * 1e6)); // Assuming 6 decimals

  switch (platform) {
    case 'pump.fun': {
      if (action === 'buy') {
        const quote = await PumpFun.getBuyQuote(connection, mint, solLamports, slippageBps);
        return {
          expectedOutput: quote.expectedTokens,
          minOutput: quote.minTokensOut,
          priceImpact: quote.priceImpact,
          isGraduated: quote.isGraduated,
          platform,
        };
      } else {
        const quote = await PumpFun.getSellQuote(connection, mint, tokenUnits, slippageBps);
        return {
          expectedOutput: quote.expectedSol,
          minOutput: quote.minSolOut,
          priceImpact: quote.priceImpact,
          isGraduated: quote.isGraduated,
          platform,
        };
      }
    }

    case 'pumpswap': {
      if (action === 'buy') {
        const quote = await PumpSwap.getBuyQuote(connection, mint, solLamports, slippageBps);
        return {
          expectedOutput: quote.expectedTokens,
          minOutput: quote.minTokensOut,
          priceImpact: quote.priceImpact,
          isGraduated: true,
          platform,
        };
      } else {
        const quote = await PumpSwap.getSellQuote(connection, mint, tokenUnits, slippageBps);
        return {
          expectedOutput: quote.expectedSol,
          minOutput: quote.minSolOut,
          priceImpact: quote.priceImpact,
          isGraduated: true,
          platform,
        };
      }
    }

    case 'letsbonk': {
      if (action === 'buy') {
        const quote = await LetsBonk.getBuyQuote(connection, mint, solLamports, slippageBps);
        return {
          expectedOutput: quote.expectedTokens,
          minOutput: quote.minTokensOut,
          priceImpact: quote.priceImpact,
          isGraduated: quote.isGraduated,
          platform,
        };
      } else {
        const quote = await LetsBonk.getSellQuote(connection, mint, tokenUnits, slippageBps);
        return {
          expectedOutput: quote.expectedSol,
          minOutput: quote.minSolOut,
          priceImpact: quote.priceImpact,
          isGraduated: quote.isGraduated,
          platform,
        };
      }
    }

    case 'raydium': {
      if (action === 'buy') {
        const quote = await Raydium.getBuyQuote(connection, mint, solLamports, slippageBps);
        return {
          expectedOutput: quote.expectedTokens,
          minOutput: quote.minTokensOut,
          priceImpact: quote.priceImpact,
          isGraduated: quote.isGraduated,
          platform,
        };
      } else {
        const quote = await Raydium.getSellQuote(connection, mint, tokenUnits, slippageBps);
        return {
          expectedOutput: quote.expectedSol,
          minOutput: quote.minSolOut,
          priceImpact: quote.priceImpact,
          isGraduated: quote.isGraduated,
          platform,
        };
      }
    }

    case 'jupiter': {
      if (action === 'buy') {
        const quote = await Jupiter.getBuyQuote(tokenAddress, solLamports, slippageBps);
        return {
          expectedOutput: quote.expectedTokens,
          minOutput: quote.minTokensOut,
          priceImpact: quote.priceImpact,
          isGraduated: true,
          platform,
          route: quote.route.routePlan.map(r => r.swapInfo.label).join(' → '),
        };
      } else {
        const quote = await Jupiter.getSellQuote(tokenAddress, tokenUnits, slippageBps);
        return {
          expectedOutput: quote.expectedSol,
          minOutput: quote.minSolOut,
          priceImpact: quote.priceImpact,
          isGraduated: true,
          platform,
          route: quote.route.routePlan.map(r => r.swapInfo.label).join(' → '),
        };
      }
    }

    default:
      throw new Error(`Unknown platform: ${platform}`);
  }
}

// ============================================================================
// Trade Execution
// ============================================================================

/**
 * Execute a trade
 */
export async function executeTrade(params: TradeParams): Promise<TradeResult> {
  const {
    tokenAddress,
    action,
    amount,
    userWallet,
    signTransaction,
    slippageBps,
    priorityLevel = 'medium',
  } = params;
  let { platform } = params;

  const mint = new PublicKey(tokenAddress);

  // Auto-detect platform
  if (!platform || platform === 'unknown') {
    const detected = await detectPlatform(tokenAddress);
    platform = detected.platform;

    if (platform === 'unknown') {
      throw new Error('Could not detect platform for this token');
    }
  }

  // Get quote first
  const quote = await getTradeQuote({
    tokenAddress,
    action,
    amount,
    slippageBps,
    platform,
  });

  // Handle Jupiter separately (uses VersionedTransaction)
  if (platform === 'jupiter') {
    return executeJupiterTrade({
      tokenAddress,
      action,
      amount,
      userWallet,
      signTransaction,
      slippageBps,
      priorityLevel,
      quote,
    });
  }

  // Build legacy transaction for bonding curve platforms
  const tx = new Transaction();

  // 1. Add priority fee
  const priorityFee = PRIORITY_FEES[priorityLevel];
  if (priorityFee > 0) {
    tx.add(
      ComputeBudgetProgram.setComputeUnitLimit({ units: 200000 }),
      ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priorityFee })
    );
  }

  // 2. Add SKOP 1% fee on every trade (buy and sell)
  if (action === 'buy') {
    const feeAmount = amount * (TRADE_FEE_BPS / 10000);
    const feeLamports = Math.floor(feeAmount * LAMPORTS_PER_SOL);

    if (feeLamports > 0) {
      tx.add(
        SystemProgram.transfer({
          fromPubkey: userWallet,
          toPubkey: SKOP_FEE_WALLET,
          lamports: feeLamports,
        })
      );
    }
  }
  // For sells, fee is deducted from SOL received (handled post-trade or via separate transfer)
  // TODO: Add sell fee transfer after trade confirmation

  // 3. Build platform-specific trade instruction
  const solLamports = BigInt(Math.floor(amount * LAMPORTS_PER_SOL));
  const tokenUnits = BigInt(Math.floor(amount * 1e6));

  let instruction: TransactionInstruction;

  switch (platform) {
    case 'pump.fun':
      instruction = action === 'buy'
        ? await PumpFun.buildBuyInstruction({
            mint,
            userWallet,
            solAmount: solLamports,
            minTokensOut: quote.minOutput,
          })
        : await PumpFun.buildSellInstruction({
            mint,
            userWallet,
            tokenAmount: tokenUnits,
            minSolOut: quote.minOutput,
          });
      break;

    case 'pumpswap':
      instruction = action === 'buy'
        ? await PumpSwap.buildBuyInstruction({
            mint,
            userWallet,
            solAmount: solLamports,
            minTokensOut: quote.minOutput,
          })
        : await PumpSwap.buildSellInstruction({
            mint,
            userWallet,
            tokenAmount: tokenUnits,
            minSolOut: quote.minOutput,
          });
      break;

    case 'letsbonk':
      instruction = action === 'buy'
        ? await LetsBonk.buildBuyInstruction({
            mint,
            userWallet,
            solAmount: solLamports,
            minTokensOut: quote.minOutput,
          })
        : await LetsBonk.buildSellInstruction({
            mint,
            userWallet,
            tokenAmount: tokenUnits,
            minSolOut: quote.minOutput,
          });
      break;

    case 'raydium':
      instruction = action === 'buy'
        ? await Raydium.buildBuyInstruction({
            mint,
            userWallet,
            solAmount: solLamports,
            minTokensOut: quote.minOutput,
          })
        : await Raydium.buildSellInstruction({
            mint,
            userWallet,
            tokenAmount: tokenUnits,
            minSolOut: quote.minOutput,
          });
      break;

    default:
      throw new Error(`Cannot build instruction for platform: ${platform}`);
  }

  tx.add(instruction);

  // 4. Set recent blockhash and fee payer
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
  tx.recentBlockhash = blockhash;
  tx.feePayer = userWallet;

  // 5. Sign and send
  const signedTx = await signTransaction(tx);
  const signature = await connection.sendRawTransaction(signedTx.serialize(), {
    skipPreflight: false,
    preflightCommitment: 'confirmed',
  });

  // 6. Confirm
  await connection.confirmTransaction({
    signature,
    blockhash,
    lastValidBlockHeight,
  });

  // 7. Track the trade for usage statistics
  // Estimate USD value (would use real price in production)
  const estimatedVolumeUsd = amount * 200; // Rough SOL/USD estimate
  trackTrade(userWallet.toString(), estimatedVolumeUsd);

  return {
    signature,
    success: true,
    expectedOutput: quote.expectedOutput.toString(),
    minOutput: quote.minOutput.toString(),
    priceImpact: quote.priceImpact,
    platform,
    explorerUrl: `https://solscan.io/tx/${signature}`,
  };
}

/**
 * Execute Jupiter trade (uses VersionedTransaction)
 */
async function executeJupiterTrade(params: {
  tokenAddress: string;
  action: 'buy' | 'sell';
  amount: number;
  userWallet: PublicKey;
  signTransaction: <T extends Transaction | VersionedTransaction>(tx: T) => Promise<T>;
  slippageBps: number;
  priorityLevel: PriorityLevel;
  quote: TradeQuote;
}): Promise<TradeResult> {
  const { tokenAddress, action, amount, userWallet, signTransaction, slippageBps, priorityLevel, quote } = params;

  const amountUnits = action === 'buy'
    ? BigInt(Math.floor(amount * LAMPORTS_PER_SOL))
    : BigInt(Math.floor(amount * 1e6));

  const priorityFeeLamports = Math.floor(PRIORITY_FEES[priorityLevel] * 200); // Approx for 200k CU

  const { transaction } = await Jupiter.buildSwapInstructions({
    userWallet,
    tokenMint: tokenAddress,
    action,
    amount: amountUnits,
    slippageBps,
    priorityFeeLamports,
  });

  // Sign the versioned transaction
  const signedTx = await signTransaction(transaction);

  // Send
  const signature = await connection.sendRawTransaction(signedTx.serialize(), {
    skipPreflight: false,
    preflightCommitment: 'confirmed',
  });

  // Confirm
  const latestBlockhash = await connection.getLatestBlockhash();
  await connection.confirmTransaction({
    signature,
    blockhash: latestBlockhash.blockhash,
    lastValidBlockHeight: latestBlockhash.lastValidBlockHeight,
  });

  // Track the trade for usage statistics
  const estimatedVolumeUsd = amount * 200; // Rough SOL/USD estimate
  trackTrade(userWallet.toString(), estimatedVolumeUsd);

  return {
    signature,
    success: true,
    expectedOutput: quote.expectedOutput.toString(),
    minOutput: quote.minOutput.toString(),
    priceImpact: quote.priceImpact,
    platform: 'jupiter',
    explorerUrl: `https://solscan.io/tx/${signature}`,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get user's SOL balance
 */
export async function getSolBalance(wallet: PublicKey): Promise<number> {
  const balance = await connection.getBalance(wallet);
  return balance / LAMPORTS_PER_SOL;
}

/**
 * Get user's token balance
 */
export async function getTokenBalance(
  wallet: PublicKey,
  mint: PublicKey
): Promise<{ balance: number; decimals: number }> {
  try {
    const tokenAccount = await getAssociatedTokenAddress(mint, wallet);
    const accountInfo = await connection.getTokenAccountBalance(tokenAccount);

    return {
      balance: Number(accountInfo.value.uiAmount || 0),
      decimals: accountInfo.value.decimals,
    };
  } catch {
    return { balance: 0, decimals: 6 };
  }
}

/**
 * Format token amount for display
 */
export function formatTokenAmount(amount: bigint, decimals: number = 6): string {
  const divisor = BigInt(10 ** decimals);
  const whole = amount / divisor;
  const fraction = amount % divisor;

  if (fraction === BigInt(0)) {
    return whole.toLocaleString();
  }

  const fractionStr = fraction.toString().padStart(decimals, '0').replace(/0+$/, '');
  return `${whole.toLocaleString()}.${fractionStr}`;
}

/**
 * Format SOL amount for display
 */
export function formatSolAmount(lamports: bigint): string {
  const sol = Number(lamports) / LAMPORTS_PER_SOL;
  return sol.toFixed(4);
}

/**
 * Get token price in SOL
 */
export async function getTokenPrice(tokenAddress: string): Promise<number | null> {
  // Try Jupiter first
  const price = await Jupiter.getTokenPriceInSol(tokenAddress);
  if (price !== null) {
    return price;
  }

  // Could add fallback to bonding curve price calculation
  return null;
}
